/** do not summarize imports that are outside coaching-base */

import { Injectable } from '@angular/core';
import { RouterStateSnapshot } from '@angular/router';
import { Observable, Subject } from 'rxjs';

import { UserView } from '../../coaching-detail/pillar-one/user-view.model';
import { CommitmentSummaryView } from '../../coaching-detail/pillar-two/commitment-summary-view.model';
import { ObservationSummaryContainerView } from '../../coaching-detail/pillar-two/observation-summary.model';
import { StatusView } from '../../coaching-detail/pillar-two/status-view.model';
import { TabRefId } from '../../coaching-detail/pillar-two/tab-ref-id.model';
import {
    FocusIdentifier,
    FocusShallow,
} from '../../coaching-tags/tags/tags.model';
import { ChatRequest } from '../../teams-call';
import { FocusDto } from '../general';
import { OpportunityView } from '../opportunity';

import { SwitchboardCommitmentComm } from './switchboard-commitment-comm.model';
import { SwitchboardConnector } from './switchboard-connector.model';
import { SwitchboardObservationComm } from './switchboard-observation-comm.model';
import { SwitchboardObservationReviewComm } from './switchboard-observation-review-comm.model';
import { SwitchboardPillarOneComm } from './switchboard-pillar-one-comm.model';
import { SwitchboardPillarTwoComm } from './switchboard-pillar-two-comm.model';
import { SwitchboardTranscriptSummaryComm } from './switchboard-transcript-summary-comm.model';

interface SwitchboardPillarOneCommInternal
    extends SwitchboardConnector<SwitchboardPillarOneComm>,
        SwitchboardPillarOneComm {}

interface SwitchboardPillarTwoCommInternal
    extends SwitchboardConnector<SwitchboardPillarTwoComm>,
        SwitchboardPillarTwoComm {}

interface SwitchboardCommitmentInternal
    extends SwitchboardConnector<SwitchboardCommitmentComm>,
        SwitchboardCommitmentComm {}

interface SwitchboardObservationInternal
    extends SwitchboardConnector<SwitchboardObservationComm>,
        SwitchboardObservationComm {}

interface SwitchboardObservationReviewInternal
    extends SwitchboardConnector<SwitchboardObservationReviewComm>,
        SwitchboardObservationReviewComm {}

interface SwitchboardTranscriptSummaryInternal
    extends SwitchboardConnector<SwitchboardTranscriptSummaryComm>,
        SwitchboardTranscriptSummaryComm {}

@Injectable({ providedIn: 'root' })
export class CoachingSwitchboardService {
    // only one of each at a time. last to connect wins
    private _pillarOne: SwitchboardPillarOneComm;
    private _pillarTwo: SwitchboardPillarTwoComm;
    private _observation: SwitchboardObservationComm;
    private _observationReview: SwitchboardObservationReviewComm;
    private _commitment: SwitchboardCommitmentComm;
    private _transcriptSummary: SwitchboardTranscriptSummaryComm;

    /** this might not be kept */
    private readonly _connectObservable: Subject<SwitchboardConnector<any>>;

    constructor() {
        this._connectObservable = new Subject();
        this._setup();
    }

    pillarOne: SwitchboardPillarOneCommInternal = {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        connect: <T extends SwitchboardPillarOneComm>(
            instance: SwitchboardPillarOneComm,
        ): void => {
            this._pillarOne = instance;
            this._connectObservable.next(
                instance as SwitchboardPillarOneCommInternal,
            );
        },

        disconnect: (): void => {
            this._pillarOne = null;
        },

        connected: (): boolean => {
            return !!this._pillarOne;
        },

        sbReady: (): Observable<boolean> => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._pillarOne && this._pillarOne.sbReady) {
                return this._pillarOne.sbReady();
            }
            return undefined;
        },

        sbSelectedOpportunity: (): Observable<OpportunityView> => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._pillarOne && this._pillarOne.sbSelectedOpportunity) {
                return this._pillarOne.sbSelectedOpportunity();
            }
            return undefined;
        },

        sbSelectedUser: (): Observable<UserView> => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._pillarOne && this._pillarOne.sbSelectedUser) {
                return this._pillarOne.sbSelectedUser();
            }
            return undefined;
        },
    };

    pillarTwo: SwitchboardPillarTwoCommInternal = {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        connect: <T extends SwitchboardPillarTwoComm>(
            instance: SwitchboardPillarTwoComm,
        ): void => {
            this._pillarTwo = instance;
            this._connectObservable.next(
                instance as SwitchboardPillarTwoCommInternal,
            );
        },

        disconnect: (): void => {
            this._pillarTwo = null;
        },

        connected: (): boolean => {
            return !!this._pillarTwo;
        },

        sbReady: (): Observable<boolean> => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._pillarTwo && this._pillarTwo.sbReady) {
                return this._pillarTwo.sbReady();
            }
            return undefined;
        },

        sbSelectedOpportunityHasActiveCommitment: (): boolean => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._pillarTwo &&
                this._pillarTwo.sbSelectedOpportunityHasActiveCommitment
            ) {
                return this._pillarTwo.sbSelectedOpportunityHasActiveCommitment();
            }
            return undefined;
        },

        sbSelectedOpportunityActiveCommitment:
            (): Observable<CommitmentSummaryView> => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._pillarTwo &&
                    this._pillarTwo.sbSelectedOpportunityActiveCommitment
                ) {
                    return this._pillarTwo.sbSelectedOpportunityActiveCommitment();
                }
                return undefined;
            },

        sbSelectedOpportunityActiveCommitmentSnapshot:
            (): CommitmentSummaryView => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._pillarTwo &&
                    this._pillarTwo
                        .sbSelectedOpportunityActiveCommitmentSnapshot
                ) {
                    return this._pillarTwo.sbSelectedOpportunityActiveCommitmentSnapshot();
                }
                return undefined;
            },

        sbSwitchTab: (which: TabRefId): void => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._pillarTwo && this._pillarTwo.sbSwitchTab) {
                this._pillarTwo.sbSwitchTab(which);
            }
        },

        // eslint-disable-next-line @typescript-eslint/promise-function-async
        sbReload: (which: TabRefId): Promise<void> => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._pillarTwo && this._pillarTwo.sbReload) {
                return this._pillarTwo.sbReload(which);
            }
            return undefined;
        },

        sbSetActive: (which: TabRefId, id: number | string): void => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._pillarTwo && this._pillarTwo.sbSetActive) {
                this._pillarTwo.sbSetActive(which, id);
            }
        },

        // eslint-disable-next-line @typescript-eslint/promise-function-async
        sbSetStatuses: (statuses: StatusView[]): Promise<void> => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._pillarTwo && this._pillarTwo.sbSetStatuses) {
                return this._pillarTwo.sbSetStatuses(statuses);
            }
            return undefined;
        },

        sbScrollIntoView: (
            which: TabRefId,
            id: number | string,
            identifier?: FocusIdentifier,
            method?: 'instant' | 'smooth',
        ): void => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._pillarTwo && this._pillarTwo.sbScrollIntoView) {
                this._pillarTwo.sbScrollIntoView(which, id, identifier, method);
            }
        },

        sbOrderedObservations: (): Observable<
            ObservationSummaryContainerView[]
        > => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._pillarTwo && this._pillarTwo.sbOrderedObservations) {
                return this._pillarTwo.sbOrderedObservations();
            }
            return undefined;
        },

        sbOrderedObservationsSnapshot:
            (): ObservationSummaryContainerView[] => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._pillarTwo &&
                    this._pillarTwo.sbOrderedObservationsSnapshot
                ) {
                    return this._pillarTwo.sbOrderedObservationsSnapshot();
                }
                return undefined;
            },

        sbActiveCommitmentWithFocus: (
            focus: FocusShallow,
        ): CommitmentSummaryView => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._pillarTwo &&
                this._pillarTwo.sbActiveCommitmentWithFocus
            ) {
                return this._pillarTwo.sbActiveCommitmentWithFocus(focus);
            }
            return undefined;
        },

        sbHasActiveCommitmentWithFocus: (focus: FocusShallow): boolean => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._pillarTwo &&
                this._pillarTwo.sbHasActiveCommitmentWithFocus
            ) {
                return this._pillarTwo.sbHasActiveCommitmentWithFocus(focus);
            }
            return undefined;
        },

        sbHasActiveCommitmentWithGoal: (
            selectedMetricIds: number[],
        ): Record<number, boolean> => {
            if (this._pillarTwo?.sbHasActiveCommitmentWithGoal) {
                return this._pillarTwo.sbHasActiveCommitmentWithGoal(
                    selectedMetricIds,
                );
            }
            return undefined;
        },
    };

    observation: SwitchboardObservationInternal = {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        connect: <T extends SwitchboardObservationComm>(
            observation: SwitchboardObservationComm,
        ): void => {
            this._observation = observation;
        },

        disconnect: (): void => {
            this._observation = null;
        },

        connected: (): boolean => {
            return !!this._observation;
        },

        sbCanCancelEdit: (
            currentState?: RouterStateSnapshot,
            nextState?: RouterStateSnapshot,
        ): Observable<boolean> | Promise<boolean> | boolean => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._observation && this._observation.sbCanCancelEdit) {
                return this._observation.sbCanCancelEdit(
                    currentState,
                    nextState,
                );
            }
        },

        sbCancelEdit: (): void => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._observation && this._observation.sbCancelEdit) {
                this._observation.sbCancelEdit();
            }
        },
    };

    observationReview: SwitchboardObservationReviewInternal = {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        connect: <T extends SwitchboardObservationReviewComm>(
            observationReview: SwitchboardObservationReviewComm,
        ): void => {
            this._observationReview = observationReview;
        },

        disconnect: (): void => {
            this._observationReview = null;
        },

        connected: (): boolean => {
            return !!this._observationReview;
        },

        sbCancelEdit: (): void => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._observationReview &&
                this._observationReview.sbCancelEdit
            ) {
                this._observationReview.sbCancelEdit();
            }
        },

        sbWillNavigate: (): void => {
            // TODO let everyone know
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._observationReview &&
                this._observationReview.sbWillNavigate
            ) {
                this._observationReview.sbWillNavigate();
            }
        },
    };

    commitment: SwitchboardCommitmentInternal = {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        connect: <T extends SwitchboardCommitmentComm>(
            commitment: SwitchboardCommitmentComm,
        ): void => {
            this._commitment = commitment;
        },

        disconnect: (): void => {
            this._commitment = null;
        },

        connected: (): boolean => {
            return !!this._commitment;
        },

        sbHasMatchingFocus: (focus: FocusShallow): boolean => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._commitment && this._commitment.sbHasMatchingFocus) {
                return this._commitment.sbHasMatchingFocus(focus);
            }
        },

        sbHasMatchingCommitmentId: (commitmentId: number): boolean => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._commitment &&
                this._commitment.sbHasMatchingCommitmentId
            ) {
                return this._commitment.sbHasMatchingCommitmentId(commitmentId);
            }
            return false;
        },

        sbHasLinkedFocus: (
            focus: FocusShallow,
            observationId: number,
        ): boolean => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._commitment && this._commitment.sbHasLinkedFocus) {
                return this._commitment.sbHasLinkedFocus(focus, observationId);
            }
        },

        // eslint-disable-next-line @typescript-eslint/promise-function-async
        sbLinkFocus: (
            focus: FocusShallow,
            observationId: number,
        ): Promise<void> => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._commitment && this._commitment.sbLinkFocus) {
                return this._commitment.sbLinkFocus(focus, observationId);
            }
        },

        // eslint-disable-next-line @typescript-eslint/promise-function-async
        sbSetFocus: (focus: FocusDto): Promise<void> => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._commitment && this._commitment.sbSetFocus) {
                return this._commitment.sbSetFocus(focus);
            }
        },

        sbUnlinkFocus: (focus: FocusShallow, observationId: number): void => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._commitment && this._commitment.sbUnlinkFocus) {
                this._commitment.sbUnlinkFocus(focus, observationId);
            }
        },

        // eslint-disable-next-line @typescript-eslint/promise-function-async
        sbSave: (): Promise<void> => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._commitment && this._commitment.sbSave) {
                return this._commitment.sbSave();
            }
        },

        sbWillNavigate: (): void => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._commitment && this._commitment.sbWillNavigate) {
                this._commitment.sbWillNavigate();
            }
        },

        sbReload: (commitmentId: number, draftId: string): void => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this._commitment && this._commitment.sbReload) {
                this._commitment.sbReload(commitmentId, draftId);
            }
        },
    };

    transcriptSummary: SwitchboardTranscriptSummaryInternal = {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        connect: <T extends SwitchboardTranscriptSummaryComm>(
            instance: SwitchboardTranscriptSummaryComm,
        ): void => {
            this._transcriptSummary = instance;
            this._connectObservable.next(
                instance as SwitchboardTranscriptSummaryInternal,
            );
        },

        disconnect: (): void => {
            this._transcriptSummary = null;
        },

        connected: (): boolean => {
            return !!this._transcriptSummary;
        },

        sbCreateChat: async (opts: ChatRequest): Promise<void> => {
            return await this._transcriptSummary?.sbCreateChat(opts);
        },

        sbReload: (expandTranscriptIds?: number[]): void => {
            this._transcriptSummary?.sbReload(expandTranscriptIds);
        },
    };

    observableForConnect(): Observable<SwitchboardConnector<any>> {
        return this._connectObservable.asObservable();
    }

    /** this is a useless method to work around the false positive of
     * comm variables not having an assignment after instantiation */
    private _setup(): void {
        this._pillarOne = null;
        this._pillarTwo = null;
        this._observation = null;
        this._observationReview = null;
        this._commitment = null;
        this._transcriptSummary = null;
    }
}
